// Hebrew date + time formatting utilities.
// Displays dates like: יום ה' פרשת כי תצא - ז' אלול תשפ"ו
// Displays times in 12-hour format (2:30 PM).
import { HDate, Sedra, Locale } from "@hebcal/core";

const DOW_HEB = [
  "יום א'",   // Sunday
  "יום ב'",   // Monday
  "יום ג'",   // Tuesday
  "יום ד'",   // Wednesday
  "יום ה'",   // Thursday
  "יום ו'",   // Friday
  "שבת קודש", // Saturday
];

// Parse "YYYY-MM-DD" or Date-like → JS Date (local midnight)
function toDate(input) {
  if (!input) return null;
  if (input instanceof Date) return input;
  if (typeof input === "string") {
    // If ISO date only, treat as local
    const m = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

// Cache Sedra by year to avoid rebuilding
const sedraCache = new Map();
function getSedra(hyear) {
  if (!sedraCache.has(hyear)) {
    // il=false for Diaspora (common in US Yeshivas — matches most late-pass use)
    sedraCache.set(hyear, new Sedra(hyear, false));
  }
  return sedraCache.get(hyear);
}

// Find the Shabbat of the current week (Sun-Sat window) for parsha lookup
function shabbatOfWeek(hd) {
  // hd.getDay(): 0=Sun..6=Sat
  const dow = hd.getDay();
  const daysToSat = (6 - dow + 7) % 7;
  return new HDate(hd.abs() + daysToSat);
}

function getParshaHeb(hd) {
  const shabbat = shabbatOfWeek(hd);
  const sedra = getSedra(shabbat.getFullYear());
  const info = sedra.lookup(shabbat);
  if (!info || !Array.isArray(info.parsha) || info.parsha.length === 0) return "";
  // parsha can be ["Matot","Masei"] for combined
  const names = info.parsha.map((n) => Locale.gettext(n, "he-x-NoNikud"));
  return `פרשת ${names.join("־")}`;
}

/**
 * Format a Hebrew date string like:  יום ה' פרשת כי תצא - ז' אלול תשפ"ו
 * @param {string|Date} input – ISO date string ("YYYY-MM-DD") or Date
 * @param {{parsha?: boolean, dow?: boolean}} [opts]
 * @returns {string}
 */
export function formatHebrewDate(input, opts = {}) {
  const { parsha = true, dow = true } = opts;
  const d = toDate(input);
  if (!d) return "—";
  try {
    const hd = new HDate(d);
    const heDate = Locale.hebrewStripNikkud(hd.renderGematriya());
    const parts = [];
    if (dow) parts.push(DOW_HEB[hd.getDay()]);
    if (parsha) {
      const p = getParshaHeb(hd);
      if (p) parts.push(p);
    }
    const prefix = parts.join(" ");
    return prefix ? `${prefix} - ${heDate}` : heDate;
  } catch (e) {
    return typeof input === "string" ? input : "—";
  }
}

/**
 * Short Hebrew date: just the gematriya date (e.g. ז' אלול תשפ"ו).
 */
export function formatHebrewDateShort(input) {
  const d = toDate(input);
  if (!d) return "—";
  try {
    const hd = new HDate(d);
    return Locale.hebrewStripNikkud(hd.renderGematriya());
  } catch {
    return "—";
  }
}

/**
 * Convert 24-hour "HH:MM" (or Date) → 12-hour "h:MM AM/PM"
 */
export function format12Hour(input) {
  if (!input) return "—";
  let hh, mm;
  if (input instanceof Date) {
    hh = input.getHours();
    mm = input.getMinutes();
  } else if (typeof input === "string") {
    const parts = input.split(":");
    if (parts.length < 2) return input;
    hh = parseInt(parts[0], 10);
    mm = parseInt(parts[1], 10);
    if (isNaN(hh) || isNaN(mm)) return input;
  } else {
    return "—";
  }
  const period = hh >= 12 ? "PM" : "AM";
  let h12 = hh % 12;
  if (h12 === 0) h12 = 12;
  const mmStr = String(mm).padStart(2, "0");
  return `${h12}:${mmStr} ${period}`;
}

/**
 * 12-hour time with Hebrew AM/PM: e.g. "7:29 אחה״צ" / "9:15 לפנה״צ"
 */
export function format12HourHeb(input) {
  if (!input) return "—";
  let hh, mm;
  if (input instanceof Date) {
    hh = input.getHours();
    mm = input.getMinutes();
  } else if (typeof input === "string") {
    const parts = input.split(":");
    if (parts.length < 2) return input;
    hh = parseInt(parts[0], 10);
    mm = parseInt(parts[1], 10);
    if (isNaN(hh) || isNaN(mm)) return input;
  } else {
    return "—";
  }
  const period = hh >= 12 ? "אחה״צ" : "לפנה״צ";
  let h12 = hh % 12;
  if (h12 === 0) h12 = 12;
  const mmStr = String(mm).padStart(2, "0");
  return `${h12}:${mmStr} ${period}`;
}

/**
 * Full date + time line for a pass, both Hebrew date and 12-hour time.
 */
export function formatPassDateTime(date, time) {
  return `${formatHebrewDate(date)} · ${format12Hour(time)}`;
}
